import { useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { FileDown, Loader2 } from "lucide-react";
import { Button } from "@app/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import API from "@app/services/api";

interface Invoice {
  id: string;
  number: string;
  // The invoicing service's status: unpaid, paid or canceled.
  status: string;
  total_cents: number;
  currency: string;
  issue_date: string;
  due_date: string;
}

interface InvoicesResponse {
  invoices: Invoice[];
}

interface PdfResponse {
  url: string;
}

function isInvoicesResponse(res: unknown): res is InvoicesResponse {
  return (
    typeof res === "object" &&
    res !== null &&
    Array.isArray((res as InvoicesResponse).invoices)
  );
}

function isPdfResponse(res: unknown): res is PdfResponse {
  return (
    typeof res === "object" &&
    res !== null &&
    typeof (res as PdfResponse).url === "string"
  );
}

/**
 * Invoices of an organisation (PT-118), for its gestionnaire only.
 *
 * The PDF link is asked for at the click: the invoicing service's links last thirty minutes.
 */
export default function BillingInvoices({
  organisationId,
}: {
  organisationId: string;
}) {
  const { t, i18n } = useLingui();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState("");
  // Not a failure: the demo's invoices are fictitious and have no PDF to open.
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setLoading(true);
    API.get({ path: `/billing/${organisationId}/invoices` })
      .then((res: unknown) => {
        setInvoices(isInvoicesResponse(res) ? res.invoices : null);
      })
      .finally(() => setLoading(false));
  }, [organisationId]);

  const amount = (invoice: Invoice) =>
    new Intl.NumberFormat(i18n.locale, {
      style: "currency",
      currency: invoice.currency,
    }).format(invoice.total_cents / 100);

  // Dates come as YYYY-MM-DD; read them as local days, not UTC midnights.
  const day = (iso: string) =>
    new Intl.DateTimeFormat(i18n.locale).format(new Date(`${iso}T00:00:00`));

  const statusLabel = (status: string) => {
    if (status === "paid") return t`Réglée`;
    if (status === "canceled") return t`Annulée`;
    return t`À régler`;
  };

  function openPdf(invoice: Invoice) {
    setOpening(invoice.id);
    setError("");
    setNotice("");
    API.get({ path: `/billing/${organisationId}/invoices/${invoice.id}/pdf` })
      .then((res: unknown) => {
        if (isPdfResponse(res)) {
          window.location.assign(res.url);
          return;
        }
        const detail = (res as { detail?: string } | null)?.detail;
        if (detail === "facture_de_demonstration") {
          setNotice(t`Facture fictive de démonstration : elle n'a pas de PDF.`);
          return;
        }
        setError(
          detail === "pdf_en_preparation"
            ? t`Le PDF de cette facture est en cours de génération : réessayez dans quelques secondes.`
            : t`Le PDF est momentanément indisponible. Réessayez plus tard.`,
        );
      })
      .finally(() => setOpening(null));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Trans>Factures</Trans>
        </CardTitle>
        <CardDescription>
          <Trans>
            Les factures sont envoyées à l'adresse e-mail de facturation et se
            règlent par virement, aux coordonnées indiquées sur chacune.
          </Trans>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && (
          <p className="text-sm text-slate-500">
            <Trans>Chargement...</Trans>
          </p>
        )}

        {!loading && invoices === null && (
          <p className="text-sm text-red-600" role="alert">
            <Trans>Impossible de charger les factures.</Trans>
          </p>
        )}

        {!loading && invoices?.length === 0 && (
          <p className="text-sm text-slate-500">
            <Trans>Aucune facture pour le moment.</Trans>
          </p>
        )}

        {!loading && invoices && invoices.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    <Trans>Numéro</Trans>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <Trans>Émise le</Trans>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <Trans>Échéance</Trans>
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    <Trans>Montant TTC</Trans>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <Trans>Statut</Trans>
                  </th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="px-3 py-2 font-medium text-slate-900">
                      {invoice.number}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {day(invoice.issue_date)}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {day(invoice.due_date)}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-900">
                      {amount(invoice)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          invoice.status === "unpaid"
                            ? "rounded-full border border-primary/50 bg-primary/5 px-2 py-0.5 text-xs font-medium text-primary"
                            : "rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                        }
                      >
                        {statusLabel(invoice.status)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPdf(invoice)}
                        disabled={opening === invoice.id}
                        aria-label={t`Télécharger la facture ${invoice.number}`}
                      >
                        {opening === invoice.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <FileDown className="h-4 w-4" />
                        )}
                        <span className="ml-1.5">
                          <Trans>PDF</Trans>
                        </span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {notice && <p className="text-sm text-slate-600">{notice}</p>}
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
